import { useEffect, useRef, useState } from 'react'
import { useUpdateGoal } from '../mutations/useUpdateGoal.js'
import { useIsReadOnly } from '../store/archiveViewStore.js'

/**
 * StatusPicker — small inline pill that opens a popover for the DRI to
 * classify a goal's health for the scorecard.
 *
 * Five values, plus the implicit "no status" → renders as 'on-track':
 *   on-track     — default, no concern
 *   at-risk      — DRI sees a risk that may turn into off-track
 *   off-track    — won't hit target without intervention
 *   done         — completed, retired from active discussion
 *   not-started  — hasn't begun yet (rare — usually 'on-track' is fine)
 *
 * The status is goal-level (lives on q2-goals-data via /goals/field) so
 * it carries forward through the Wednesday wipe. The wipe only touches
 * per-week content (weeklySummary, teamUpdates, execSummary, discussFlags).
 */

export const STATUS_OPTIONS = [
  { value: 'on-track', label: 'on track', color: '#16a34a', bg: '#dcfce7', text: '#166534' },
  { value: 'at-risk', label: 'at risk', color: '#d97706', bg: '#fef3c7', text: '#854d0e' },
  { value: 'off-track', label: 'off track', color: '#dc2626', bg: '#fee2e2', text: '#991b1b' },
  { value: 'done', label: 'done', color: '#6b7280', bg: '#f3f4f6', text: '#374151' },
  { value: 'not-started', label: 'not started', color: '#9ca3af', bg: '#f9fafb', text: '#4b5563' },
]

const FALLBACK = STATUS_OPTIONS[0]

/**
 * Resolve a goal.status string to a STATUS_OPTIONS entry. Anything
 * unrecognized (including undefined/null) falls back to 'on-track'.
 */
export function resolveStatus(value) {
  if (!value) return FALLBACK
  return STATUS_OPTIONS.find((s) => s.value === value) || FALLBACK
}

export default function StatusPicker({ pillarName, goalIndex, value }) {
  const readOnly = useIsReadOnly()
  const updateGoal = useUpdateGoal()
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef(null)

  const current = resolveStatus(value)

  // Close on outside click + Esc
  useEffect(() => {
    if (!open) return
    const onDoc = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const pick = (next) => {
    setOpen(false)
    if (next === value || (!value && next === 'on-track')) return
    updateGoal.mutate({ pillarName, goalIndex, field: 'status', value: next })
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          if (!readOnly) setOpen((x) => !x)
        }}
        disabled={readOnly}
        title={readOnly ? 'Status is read-only in archive view' : 'Set goal status'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '2px 8px',
          borderRadius: 99,
          background: current.bg,
          color: current.text,
          border: `1px solid ${current.color}30`,
          fontSize: 10,
          fontWeight: 600,
          cursor: readOnly ? 'default' : 'pointer',
          letterSpacing: 0.02,
          whiteSpace: 'nowrap',
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: current.color,
            display: 'inline-block',
          }}
        />
        {current.label}
        {!readOnly && (
          <span style={{ fontSize: 8, color: current.color, opacity: 0.7 }}>▾</span>
        )}
      </button>

      {open && !readOnly && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: 140,
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            padding: 4,
            zIndex: 100,
          }}
        >
          {STATUS_OPTIONS.map((opt) => {
            const isCurrent = opt.value === current.value
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => pick(opt.value)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 10px',
                  border: 'none',
                  background: isCurrent ? '#f3f4f6' : 'transparent',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                  color: '#1f2937',
                }}
                onMouseEnter={(e) => {
                  if (!isCurrent) e.currentTarget.style.background = '#f9fafb'
                }}
                onMouseLeave={(e) => {
                  if (!isCurrent) e.currentTarget.style.background = 'transparent'
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: opt.color,
                    flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1 }}>{opt.label}</span>
                {isCurrent && (
                  <span style={{ fontSize: 11, color: '#6b7280' }}>✓</span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
